import { useState } from "react";
import { FaEnvelope, FaWhatsapp, FaGlobe, FaLinkedin } from "react-icons/fa";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus(null);
    setFormError("");

    const form = e.currentTarget;
    const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

    if (!accessKey) {
      setFormStatus("error");
      setFormError(
        "Email is not configured. Add REACT_APP_WEB3FORMS_ACCESS_KEY to a .env file (see .env.example)."
      );
      return;
    }

    const formData = new FormData(form);
    const name = (formData.get("name") || "").toString().trim();
    const email = (formData.get("email") || "").toString().trim();
    const message = (formData.get("message") || "").toString().trim();

    if (!name || !email || !message) {
      setFormStatus("error");
      setFormError("Please fill in name, email, and message.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: accessKey,
          subject: `Portfolio contact from ${name}`,
          name,
          email,
          message,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Something went wrong. Please try again.");
      }

      setFormStatus("success");
      form.reset();
    } catch (err) {
      setFormStatus("error");
      setFormError(err instanceof Error ? err.message : "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="section-header">
        <p className="section-kicker">Contact</p>
        <h2 className="section-title">Let&apos;s build something together</h2>
        <p className="section-description">
          Open to roles where I can contribute to complex frontends, automation, and product
          engineering.
        </p>
      </div>

      <div className="contact-layout">
        <article className="card">
          <div className="card-header">
            <div className="card-icon">
              <FaEnvelope />
            </div>
            <div>
              <div className="card-title">Contact details</div>
              <div className="card-subtitle">Reach out via email, WhatsApp, or LinkedIn</div>
            </div>
          </div>
          <div className="card-body">
            <div className="contact-info-list">
              <div className="contact-info-item">
                <span className="contact-info-label">Email</span>
                <span className="contact-info-value">
                  <a href="mailto:jtahir21@gmail.com">jtahir21@gmail.com</a>
                </span>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">Phone / WhatsApp</span>
                <span className="contact-info-value">
                  <a href="https://wa.me/919674464995" target="_blank" rel="noreferrer">
                    9674464995
                  </a>
                </span>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">Portfolio</span>
                <span className="contact-info-value">
                  <a href="https://tahirjamal.com" target="_blank" rel="noreferrer">
                    tahirjamal.com
                  </a>
                </span>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">LinkedIn</span>
                <span className="contact-info-value">
                  <a
                    href="https://linkedin.com/in/tahir-jamal-cyb16/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    linkedin.com/in/tahir-jamal
                  </a>
                </span>
              </div>
            </div>
          </div>
        </article>

        <article className="card">
          <div className="card-header">
            <div className="card-icon">
              <FaGlobe />
            </div>
            <div>
              <div className="card-title">Send a quick message</div>
              <div className="card-subtitle">Share a short brief and I&apos;ll get back soon</div>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="field-group">
              <label className="field-label" htmlFor="name">
                Name
              </label>
              <input
                id="name"
                className="field-input"
                type="text"
                name="name"
                placeholder="Your name"
              />
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="field-input"
                type="email"
                name="email"
                placeholder="you@example.com"
              />
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="message">
                Project details
              </label>
              <textarea
                id="message"
                className="field-textarea"
                name="message"
                placeholder="Briefly describe what you'd like to build or improve."
              />
            </div>

            <div className="contact-submit-row">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                <FaEnvelope size={14} />
                <span>{submitting ? "Sending…" : "Send Message"}</span>
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() =>
                  window.open("https://wa.me/919674464995", "_blank", "noopener,noreferrer")
                }
              >
                <FaWhatsapp size={14} />
                <span>WhatsApp</span>
              </button>
            </div>
            {formStatus === "success" && (
              <p className="contact-status contact-status--success" role="status">
                Thanks — your message was sent. I&apos;ll reply soon.
              </p>
            )}
            {formStatus === "error" && formError && (
              <p className="contact-status contact-status--error" role="alert">
                {formError}
              </p>
            )}
            <p className="contact-helper">
              Messages are delivered to my inbox. You can also reach me via email or WhatsApp above.
            </p>
          </form>
        </article>
      </div>
    </>
  );
}
